import type {recordRumDurationVital} from './datadogRum';

const INPUT_DURATION_THRESHOLD_MS = 16;
const ACTION_SELECTOR =
  '[data-perf-action], button, a, input, textarea, select';

/**
 * Reports click/keydown input-to-next-paint latency for the lifetime of RUM.
 * All durations are milliseconds:
 * - duration: total time from input creation through the next paint, including
 *   input delay, event processing, and presentation delay.
 * - inputDelayMs: time before event dispatch begins, often waiting for busy
 *   main-thread work to finish.
 * - processingDurationMs: elapsed time from the start to the end of event
 *   dispatch, including event handler execution.
 * - presentationDelayMs: time after dispatch until the next paint. This can
 *   include other work, rendering, and scheduling; it is not rendering CPU time.
 *   It is approximate because duration is rounded to 8 ms, so clamp negative
 *   remainders to zero. The phases may not sum exactly to the rounded total.
 *
 * startTime anchors the original input timestamp to the Unix epoch.
 * inputType identifies click or keydown; tagName and action identify the closest
 * matching control and its data-perf-action attribute when available.
 * These timings do not measure completion of asynchronous application work.
 */
export function observeRumInputLatency(
  report: typeof recordRumDurationVital
): (() => void) | undefined {
  if (
    typeof PerformanceObserver === 'undefined' ||
    !PerformanceObserver.supportedEntryTypes?.includes('event')
  ) {
    return undefined;
  }

  const observer = new PerformanceObserver(list => {
    for (const entry of list.getEntries() as PerformanceEventTiming[]) {
      if (
        (entry.name !== 'click' && entry.name !== 'keydown') ||
        entry.duration < INPUT_DURATION_THRESHOLD_MS
      ) {
        continue;
      }

      const target = entry.target;
      const actionElement =
        target instanceof Element ? target.closest(ACTION_SELECTOR) : null;

      report('input_latency', {
        startTime: performance.timeOrigin + entry.startTime,
        duration: entry.duration,
        context: {
          action: actionElement?.getAttribute('data-perf-action'),
          inputDelayMs: entry.processingStart - entry.startTime,
          inputType: entry.name,
          processingDurationMs: entry.processingEnd - entry.processingStart,
          tagName: actionElement?.tagName,
          target,
          presentationDelayMs: Math.max(
            0,
            entry.startTime + entry.duration - entry.processingEnd
          ),
        },
      });
    }
  });

  const options: PerformanceObserverInit & {durationThreshold: number} = {
    type: 'event',
    buffered: true,
    durationThreshold: INPUT_DURATION_THRESHOLD_MS,
  };
  observer.observe(options);
  return () => observer.disconnect();
}
